import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { OwnerType, OwnerTypes } from "@/domain/_shared/lib/owner-types";
import { ClimateAction } from "@/domain/climate-action/models/climate-action";
import { DatatableFocusAreaTemplateColumn } from "@/domain/focus-area/components/datatable-focus-area-column/datatable-focus-area-template-column";
import { FocusAreaInstance } from "@/domain/focus-area/models/focus-area-model";
import { Project } from "@/domain/project/models/project-model";
import { DatatableMultiAssigneeTemplateColumn } from "@/domain/user/components/datatable-assignee-column/datatable-multi-assignee-template-column";
import { Assignee } from "@/domain/user/models/assignee-model";
import { DatatableCellTitle } from "@/domain/view/components/datatable/datatable-template-cells/datatable-cell-title";
import { DatatableColumnHeaderCell } from "@/domain/view/components/datatable/datatable-template-cells/datatable-column-header-cell";
import { DatatableColumn, DatatableRowData, isRawRow } from "@/domain/view/components/datatable/datatable-types";
import { sortRowsByTitle } from "@/domain/view/components/datatable/datatable-utils/sort-rows-by-title";
import { Workspace } from "@/domain/workspace/models/workspace-model";

import { CheckboxCell } from "./checkbox-cell";
import { CheckboxHeaderCell } from "./checkbox-header-cell";
import { type SelectedEntity } from "./create-data-request-state";

const COLUMN_ID_CHECKBOX = "checkbox";
const COLUMN_ID_TITLE = "title";
export const COLUMN_ID_FOCUS_AREA = "focus_area";

type EntityType = SelectedEntity["entityType"];

export interface SelectableRow extends DatatableRowData {
  id: string;
  title: string;
  assigneeList?: { userId?: string; user?: { fullname?: string | null } | null }[] | null;
}

function toSelectedEntity(row: SelectableRow, entityType: EntityType): SelectedEntity {
  const singleAssignee = row.assigneeList?.length === 1 ? row.assigneeList[0] : null;

  return {
    entityId: row.id,
    entityType,
    entityTitle: row.title,
    assigneeId: singleAssignee?.userId ?? null,
    assigneeName: singleAssignee?.user?.fullname ?? null,
    assigneeList: (row.assigneeList ?? [])
      .filter((a): a is typeof a & { userId: string } => !!a.userId)
      .map((a) => ({
        userId: a.userId,
        fullname: a.user?.fullname ?? null,
      })),
  };
}

function createCheckboxColumn<TData extends SelectableRow>(
  allEntityList: TData[],
  entityType: EntityType,
): DatatableColumn<TData> {
  const selectableEntityList = allEntityList.map((entity) => toSelectedEntity(entity, entityType));

  return {
    columnId: COLUMN_ID_CHECKBOX,
    getDefaultVisibilityState: () => true,
    getDisplayValue: () => "",
    getOrderValue: () => 1,
    getColumnDef: () => ({
      id: COLUMN_ID_CHECKBOX,
      enableHiding: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 40,
      header: () => <CheckboxHeaderCell entityType={entityType} allEntityList={selectableEntityList} />,
      cell: ({ row }) => {
        if (!isRawRow(row.original)) {
          return null;
        }
        return <CheckboxCell entity={toSelectedEntity(row.original, entityType)} entityType={entityType} />;
      },
    }),
    renderDisplayView: () => <></>,
    getFilterConfigurationViewType: () => "none",
    renderFilterConfigurationView: () => null,
    renderAppliedFilterView: () => <></>,
  };
}

function createSimplifiedTitleColumn<TData extends SelectableRow>(
  displayValue: string,
  noTitleText: string,
): DatatableColumn<TData> {
  return {
    columnId: COLUMN_ID_TITLE,
    getDefaultVisibilityState: () => true,
    getDisplayValue: () => displayValue,
    getOrderValue: () => 2,
    getColumnDef: () => ({
      id: COLUMN_ID_TITLE,
      accessorKey: COLUMN_ID_TITLE,
      enableHiding: false,
      enableSorting: true,
      enableColumnFilter: false,
      sortingFn: sortRowsByTitle,
      header: ({ column }) => (
        <DatatableColumnHeaderCell column={column} className="pr-1 text-xs font-normal">
          {displayValue}
        </DatatableColumnHeaderCell>
      ),
      cell: ({ row }) => {
        if (!isRawRow(row.original)) {
          return null;
        }
        const title = row.original.title;
        return (
          <DatatableCellTitle row={row} className="max-w-120">
            <span className="my-1 max-w-full overflow-hidden text-ellipsis whitespace-nowrap font-medium">
              {title !== "" ? title : <span className="text-muted-foreground">{noTitleText}</span>}
            </span>
          </DatatableCellTitle>
        );
      },
    }),
    renderDisplayView: () => <></>,
    getFilterConfigurationViewType: () => "none",
    renderFilterConfigurationView: () => null,
    renderAppliedFilterView: () => <></>,
  };
}

interface EntityWithRelations extends DatatableRowData {
  id: string;
  title: string;
  assigneeList?: Assignee[] | null;
  teamInstanceList?: { teamId: string }[] | null;
  focusAreaInstance?: FocusAreaInstance | null;
}

function useEntitySelectionColumns<TData extends EntityWithRelations>({
  workspace,
  list,
  namespace,
  ownerType,
}: {
  workspace: Workspace;
  list: TData[];
  namespace: "climate-action" | "project";
  ownerType: OwnerType;
}): DatatableColumn<TData>[] {
  const { t } = useDomainTranslation(namespace, { workspaceId: workspace.id });

  const entityType: EntityType = ownerType === OwnerTypes.CLIMATE_ACTION ? "CLIMATE_ACTION" : "PROJECT";

  const checkboxColumn = createCheckboxColumn<TData>(list, entityType);
  const titleColumn = createSimplifiedTitleColumn<TData>(t("columns.title"), t("columns.noTitle"));

  const assigneeColumn = DatatableMultiAssigneeTemplateColumn<TData>({
    workspace,
    columnId: "assignee",
    orderValue: 3,
    displayValue: t("columns.assignee"),
    editable: false,
    ownerTypeFromRow: () => ownerType,
    assigneeListFromRow: (row) => row.assigneeList ?? [],
    ownerIdFromRow: (row) => row.id,
    teamIdListForRow: (row) => row.teamInstanceList?.map((ti) => ti.teamId) ?? [],
  });

  const focusAreaColumn = DatatableFocusAreaTemplateColumn<TData>({
    workspace,
    columnId: COLUMN_ID_FOCUS_AREA,
    orderValue: 4,
    displayValue: t("columns.focusArea"),
    editable: false,
    focusAreaInstanceFromRow: (row) => row.focusAreaInstance ?? null,
    ownerIdFromRow: (row) => row.id,
  });

  return [checkboxColumn, titleColumn, assigneeColumn, focusAreaColumn];
}

export function useClimateActionSelectionColumns({
  workspace,
  climateActionList,
}: {
  workspace: Workspace;
  climateActionList: ClimateAction[];
}): DatatableColumn<ClimateAction>[] {
  return useEntitySelectionColumns<ClimateAction>({
    workspace,
    list: climateActionList,
    namespace: "climate-action",
    ownerType: OwnerTypes.CLIMATE_ACTION,
  });
}

export function useProjectSelectionColumns({
  workspace,
  projectList,
}: {
  workspace: Workspace;
  projectList: Project[];
}): DatatableColumn<Project>[] {
  return useEntitySelectionColumns<Project>({
    workspace,
    list: projectList,
    namespace: "project",
    ownerType: OwnerTypes.PROJECT,
  });
}

export function useIndicatorSelectionColumns({
  indicatorList,
}: {
  indicatorList: SelectableRow[];
}): DatatableColumn<SelectableRow>[] {
  const { t } = useDomainTranslation("data-request");

  const checkboxColumn = createCheckboxColumn<SelectableRow>(indicatorList, "INDICATOR");
  const titleColumn = createSimplifiedTitleColumn<SelectableRow>(
    t("create.fields.indicatorTitle"),
    t("create.indicatorNoTitle"),
  );

  return [checkboxColumn, titleColumn];
}

export { toSelectedEntity };
